import { useState, useMemo } from 'react';
import { HiArrowPath, HiXMark } from 'react-icons/hi2';
import CustomDropdown from '../../components/CustomDropdown/CustomDropdown';
import MultiSelectDropdown from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import './StoreFilters.css';

export interface FilterState {
  storeType: string;
  radius: number;
  channels: string[];
  mapType: string;
  showOption: string;
  storesHave: string[];
  storesDontHave: string[];
  showEntireNeighborhood: boolean;
  limitGeofence: boolean;
}

interface StoreFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  nearbyStoresCount?: number;
  nearbyStores?: any[];
  referenceBrand?: string;
  onClose?: () => void;
  availableChannels?: string[];
}

const SHOW_OPTIONS = ['All Stores in Neighbourhood', 'Located in Shopping Complex'];
const SOLUTION_OPTIONS = ['P', 'CM', 'CC', 'S'];

function StoreFilters({ filters, onFiltersChange, nearbyStores = [], onClose, availableChannels = [] }: StoreFiltersProps) {
  const [showResetBtn, setShowResetBtn] = useState(false);

  const storeCounts = useMemo(() => {
    const withinRadius = nearbyStores.filter(s => (s.distance || 0) <= filters.radius);
    const total = withinRadius.length;
    const customers = withinRadius.filter(s =>
      !!(s.purchek_install_date || s.containment_install_date || s.cart_manager_install_date || s.subscription_start_date || s.paired_wheel_install_date || s.customer_id)
    ).length;
    return { total, customers, nonCustomers: total - customers };
  }, [nearbyStores, filters.radius]);

  const handleStoreTypeChange = (value: string) => {
    const newFilters = { ...filters, storeType: value };
    onFiltersChange(newFilters);
    setShowResetBtn(true);
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFilters = { ...filters, radius: parseFloat(e.target.value) };
    onFiltersChange(newFilters);
    setShowResetBtn(true);
  };

  const handleChannelsChange = (values: string[]) => {
    onFiltersChange({ ...filters, channels: values });
    setShowResetBtn(true);
  };

  const handleShowOptionChange = (value: string) => {
    const newFilters = { ...filters, showOption: value };
    onFiltersChange(newFilters);
    setShowResetBtn(true);
  };

  const handleStoresHaveToggle = (solution: string) => {
    const newStoresHave = filters.storesHave.includes(solution)
      ? filters.storesHave.filter((s) => s !== solution)
      : [...filters.storesHave, solution];
    const newFilters = { ...filters, storesHave: newStoresHave };
    onFiltersChange(newFilters);
    setShowResetBtn(true);
  };

  const handleStoresDontHaveToggle = (solution: string) => {
    const newStoresDontHave = filters.storesDontHave.includes(solution)
      ? filters.storesDontHave.filter((s) => s !== solution)
      : [...filters.storesDontHave, solution];
    const newFilters = { ...filters, storesDontHave: newStoresDontHave };
    onFiltersChange(newFilters);
    setShowResetBtn(true);
  };

  const handleResetFilters = () => {
    const defaultFilters: FilterState = {
      storeType: 'All Stores',
      radius: 3.1,
      channels: [],
      mapType: 'Road',
      showOption: 'All Stores in Neighbourhood',
      storesHave: [],
      storesDontHave: [],
      showEntireNeighborhood: false,
      limitGeofence: false
    };
    onFiltersChange(defaultFilters);
    setShowResetBtn(false);
  };

  return (
    <div className="store-filters-container">
      {onClose && (
        <button className="filters-close-btn" onClick={onClose} title="Close filters">
          <HiXMark size={20} />
        </button>
      )}
      <div className="filters-row">
        {/* Store Type Dropdown */}
        <div className="filter-item">
          <label className="filter-label">Store Type:</label>
          <CustomDropdown
            value={filters.storeType}
            options={[
              { label: `All Stores (${storeCounts.total})`, value: 'All Stores' },
              { label: `Customers (${storeCounts.customers})`, value: 'Customers' },
              { label: `Non-Customer (${storeCounts.nonCustomers})`, value: 'Non-Customer' },
            ]}
            onChange={handleStoreTypeChange}
            className="filter-dropdown"
          />
        </div>

        {/* Radius Slider */}
        <div className="filter-item radius-item">
          <label className="filter-label">Radius:</label>
          <div className="radius-control">
            <input
              type="range"
              className="radius-slider"
              min="0.1"
              max="5"
              step="0.1"
              value={filters.radius}
              onChange={handleRadiusChange}
              disabled={filters.limitGeofence}
            />
            <span className="radius-value">{filters.radius.toFixed(1)} mi</span>
          </div>
        </div>


        {/* Channel Multi-Select */}
        <div className="filter-item">
          <label className="filter-label">Channel:</label>
          <MultiSelectDropdown
            values={filters.channels}
            options={availableChannels.filter(c => c !== 'Not Found')}
            onChange={handleChannelsChange}
            placeholder="All Channels"
            className="channel-multiselect"
            hasSelectAll={true}
          />
        </div>

        {/* Neighbourhood Store Type Dropdown */}
        <div className="filter-item">
          <label className="filter-label">Neighbourhood Store Type:</label>
          <CustomDropdown
            value={filters.showOption}
            options={SHOW_OPTIONS}
            onChange={handleShowOptionChange}
            className="filter-dropdown"
          />
        </div>

        {/* Stores have solutions */}
        <div className="filter-item solutions-item">
          <label className="filter-label">Stores have:</label>
          <div className="solutions-buttons">
            {SOLUTION_OPTIONS.map((solution) => (
              <button
                key={solution}
                className={`solution-btn ${filters.storesHave.includes(solution) ? 'active' : ''}`}
                onClick={() => handleStoresHaveToggle(solution)}
                title={`Filter by ${solution}`}
              >
                {solution}
              </button>
            ))}
          </div>
        </div>

        {/* Stores don't have solutions */}
        <div className="filter-item solutions-item">
          <label className="filter-label">Stores don't have:</label>
          <div className="solutions-buttons">
            {SOLUTION_OPTIONS.map((solution) => (
              <button
                key={solution}
                className={`solution-btn ${filters.storesDontHave.includes(solution) ? 'active' : ''}`}
                onClick={() => handleStoresDontHaveToggle(solution)}
                title={`Filter by ${solution}`}
              >
                {solution}
              </button>
            ))}
          </div>
        </div>

        {/* Reset Filters Button */}
        {showResetBtn && (
          <button className="reset-filters-btn" onClick={handleResetFilters}>
            <HiArrowPath />
            <span className="reset-btn-text">Reset Filters</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default StoreFilters;
